
import React from "react";
import { PenLine, Check } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { useValidation } from "@/contexts/ValidationContext";

interface BrandVoiceStepProps {
  brandVoice: string;
  updateBusinessData: (key: string, value: string | string[]) => void;
}

const BrandVoiceStep: React.FC<BrandVoiceStepProps> = ({ 
  brandVoice, 
  updateBusinessData 
}) => {
  const { getFieldError } = useValidation();
  const brandVoiceError = getFieldError('brandVoice');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <PenLine className="text-primary h-6 w-6" />
        <h3 className="text-2xl font-medium">Brand Voice</h3>
      </div>
      
      <Separator />

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="brand-voice">Describe Your Brand Voice <span className="text-red-500">*</span></Label>
          <Textarea
            id="brand-voice"
            placeholder="Professional and authoritative? Friendly and casual? Innovative and bold?"
            className={`resize-none ${brandVoiceError ? "border-red-500" : ""}`}
            rows={4}
            value={brandVoice}
            onChange={(e) => updateBusinessData("brandVoice", e.target.value)}
          />
          {brandVoiceError && (
            <p className="text-red-500 text-sm mt-1">{brandVoiceError}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            This helps VirtuStaff.ai create content that matches your brand's tone and style.
          </p>
        </div>
        
        <div className="mt-4 p-4 bg-primary/5 rounded-lg">
          <h4 className="text-md font-medium mb-2">Brand Voice Examples:</h4>
          <ul className="text-sm space-y-2">
            <li className="flex items-start gap-2">
              <Check size={16} className="text-primary mt-0.5" />
              <span>"Professional, authoritative, and helpful with a focus on educating our audience."</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={16} className="text-primary mt-0.5" />
              <span>"Casual, friendly, and conversational. We like to use humor when appropriate."</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={16} className="text-primary mt-0.5" />
              <span>"Innovative and forward-thinking with a touch of excitement. We want to inspire our audience."</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BrandVoiceStep;
